/**
 * Идентификаторы вкладок карточки поля.
 *
 * v3 IA (эталон founder 2026-07-11): 6 вкладок —
 * Обзор · Посевы · Геометрия · Журнал · Кадастры · Управление.
 *
 * + 'history' («Севооборот», В-1 сессии посевов 07.08) — ввод истории культур
 * по годам. Отдельная вкладка, а не раздел «Посевов»: там ведут ЖИВОЙ сезон,
 * здесь вбивают задним числом прошлые десять лет. Смешивать эти два режима
 * работы в одном экране — гарантированная правка живого посева по ошибке.
 *
 * + 'ops' («Тех. операции», AK-736, UAT founder 21.08) — полный список
 * операций поля своей вкладкой. Раньше это был легаси-подэкран Обзора
 * (строка-ссылка «все →» из виджета) — здесь тот же список, те же
 * дата/сортировка (`FieldOperationsTab` переиспользует `widgetLogic.ts`),
 * но как полноценная вкладка, а не подэкран внутри другой.
 */
export type FieldCardTab =
  | 'overview'
  | 'rotation'
  | 'history'
  | 'geometry'
  | 'journal'
  | 'cadastre'
  | 'mgmt'
  | 'ops';

/**
 * Совместимость со старыми id вкладок (11-табовая карточка). Дочерние
 * компоненты и внешние вызовы onGoto могут передавать легаси-значения —
 * ремапим на новую структуру, чтобы ничего не «проваливалось в 404».
 * 'map' → навигация на /map (обрабатывается отдельно на странице).
 */
export type LegacyFieldCardTab =
  | 'occupy'
  | 'attrs'
  | 'transfers'
  | 'terrain'
  | 'operations'
  | 'inspections'
  | 'cadastres'
  | 'map';

const LEGACY_TAB_MAP: Record<LegacyFieldCardTab, FieldCardTab> = {
  occupy: 'rotation',
  attrs: 'mgmt',
  transfers: 'mgmt',
  cadastres: 'cadastre',
  // Рельеф / осмотры пока остаются подэкранами Обзора (строки-ссылки, PR-B).
  terrain: 'overview',
  // AK-736: операции получили СВОЮ вкладку — легаси-ссылка ведёт на неё,
  // а не тонет в Обзоре.
  operations: 'ops',
  inspections: 'overview',
  // Карта переехала в кнопку «На карте» шапки — на вкладке нет, ведём на Обзор.
  map: 'overview',
};

const KNOWN_TABS: ReadonlySet<string> = new Set<FieldCardTab>([
  'overview',
  'rotation',
  'history',
  'geometry',
  'journal',
  'cadastre',
  'mgmt',
  'ops',
]);

/** Приводит любой (в т.ч. легаси) id вкладки к валидному v3-значению. */
export function resolveFieldCardTab(tab: string): FieldCardTab {
  if (KNOWN_TABS.has(tab)) return tab as FieldCardTab;
  if (tab in LEGACY_TAB_MAP) return LEGACY_TAB_MAP[tab as LegacyFieldCardTab];
  return 'overview';
}

/**
 * Вкладка, с которой открывается карточка, по query-строке (`?tab=rotation`).
 *
 * Нужна ссылкам «извне», которые ведут в конкретный раздел карточки: попап
 * поля на /map отправляет «Запланировать →» сразу на «Посевы», а не на Обзор,
 * из которого пользователю пришлось бы искать вкладку самому. Значение
 * прогоняется через resolveFieldCardTab — мусор/легаси-id падают на Обзор.
 *
 * `search` необязателен (по умолчанию — текущий URL), чтобы функция была
 * чистой и тестировалась без DOM.
 */
export function initialFieldCardTab(search?: string): FieldCardTab {
  const raw =
    search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const tab = new URLSearchParams(raw).get('tab');
  return tab ? resolveFieldCardTab(tab) : 'overview';
}
